
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import UplivionDateWheelPicker, { type DateSelection } from '@/components/ui/UplivionDateWheelPicker';
import { Badge } from '@/components/ui/badge';
import { TimelineSection } from '@/types';
import { findSectionForYear } from '@/utils/timelineSectionHelpers';
import { useTranslation } from 'react-i18next';

interface RollingDatePickerProps {
  onSelect: (dateData: DateSelection) => void;
  onYearChange?: (year: string) => void;
  onSectionChange?: (newSection: TimelineSection) => void;
  showToday: boolean;
  initialValues?: Partial<DateSelection>;
  sectionId?: string;
  timelineSections?: TimelineSection[];
  currentSectionId?: string;
  userBirthdate?: string;
  // Legacy props for backward compatibility
  selectedDate?: Date | null;
  onDateChange?: (date: Date | null) => void;
}

const RollingDatePicker = ({ 
  onSelect, 
  onYearChange,
  onSectionChange,
  showToday, 
  initialValues,
  sectionId,
  timelineSections,
  currentSectionId,
  userBirthdate,
  selectedDate,
  onDateChange
}: RollingDatePickerProps) => {
  const { i18n } = useTranslation();
  const [currentLifePhase, setCurrentLifePhase] = useState<TimelineSection | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>(initialValues?.year || null);

  console.log('🎯 RollingDatePicker render:', {
    initialValues,
    sectionId,
    showToday,
    currentSectionId,
    timelineSectionsLength: timelineSections?.length || 0,
    userBirthdate,
    hasOnSectionChange: !!onSectionChange,
    isLegacyMode: !!(selectedDate !== undefined || onDateChange)
  });

  // Initialize current life phase based on initial year or current section
  useEffect(() => {
    if (initialValues?.year && timelineSections && userBirthdate) {
      const lifePhase = findSectionForYear(initialValues.year, timelineSections, userBirthdate);
      setCurrentLifePhase(lifePhase);
    } else if (currentSectionId && timelineSections) {
      const section = timelineSections.find(s => s.id === currentSectionId);
      setCurrentLifePhase(section || null);
    }
  }, [initialValues?.year, currentSectionId, timelineSections, userBirthdate]);

  const handleYearChangeWithBadgeUpdate = (year: string) => {
    setSelectedYear(year);
    
    // Update the life phase badge
    if (year && timelineSections && userBirthdate) {
      const newLifePhase = findSectionForYear(year, timelineSections, userBirthdate);
      setCurrentLifePhase(newLifePhase);
    }
    
    // Call the original year change handler
    if (onYearChange) {
      onYearChange(year);
    }
  };

  const getAgeDisplay = (section: TimelineSection): string => {
    if (!userBirthdate || !section) return '';
    
    const birthYear = new Date(userBirthdate).getFullYear();
    const yearsText = i18n.language === 'en' ? 'years' : 'Jahre';
    
    if (selectedYear) {
      // Show specific age for selected year
      const age = parseInt(selectedYear) - birthYear;
      return ` (${age} ${yearsText})`;
    } else {
      // Show age range for the section
      const ageFrom = section.age_from;
      const ageTo = section.age_to;
      
      if (ageFrom === ageTo) {
        return ` (${ageFrom} ${yearsText})`;
      }
      return ` (${ageFrom}-${ageTo} ${yearsText})`;
    }
  };

  const handleDateSelect = (dateData: DateSelection) => {
    // Handle legacy mode
    if (onDateChange && dateData.year) {
      const year = parseInt(dateData.year);
      const month = dateData.month ? parseInt(dateData.month) - 1 : 0;
      const day = dateData.day ? parseInt(dateData.day) : 1;
      const date = new Date(year, month, day);
      onDateChange(date);
    }
    
    // Handle new mode
    onSelect(dateData);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {currentLifePhase && (
        <div className="flex justify-center">
          <Badge variant="secondary" className="text-sm px-3 py-1 bg-uplivion-blue/10 text-uplivion-blue border-uplivion-blue/20">
            {currentLifePhase.name}{getAgeDisplay(currentLifePhase)}
          </Badge>
        </div>
      )}
      
      <UplivionDateWheelPicker
        key={`date-picker-${sectionId || 'default'}`}
        onDateSelect={handleDateSelect}
        onYearChange={handleYearChangeWithBadgeUpdate}
        onSectionChange={onSectionChange}
        showToday={showToday}
        initialValues={initialValues}
        timelineSections={timelineSections}
        currentSectionId={currentSectionId}
        userBirthdate={userBirthdate}
      />
    </motion.div>
  );
};

export default RollingDatePicker;
